import React, { useEffect } from 'react';
import {
  object, string, array, mixed, date
} from 'yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';

import RightModalLayout from '../../layouts/Modal/RightModalLayout';
import FormProvierMC from '../../components/FormProviderMC';
import ButtonMC from '../../components/ButtonMC';
import Step1Partial from './_createCampaingSidebar/_step1';
import Step2QrPartial from './_createCampaingSidebar/_step2Qr';
import Step2GupshupPartial from './_createCampaingSidebar/_step2Gupshup';
import Step3Partial from './_createCampaingSidebar/_step3';
import Step4Partial from './_createCampaingSidebar/_step4';
import { INTEGRATION_TYPES } from '../../../constants/campaignsConstants';
import { getGupshupCatalogs, getQrCatalogs } from '../../../actions/campaignsActions';
import createCampaignModel from '../../../data/models/campaigns/createCampaingModel';
import { jsonToFormData } from '../../../util/jsonUtils';
import { getDateInTimezone, serializeCampaignSendAt } from '../../modules/Campaigns/utils/campaignSendAtSerialization';

/*
  // ICreateCampaignsSidebarProps
  type: string; 'GUPSHUP' | 'QR';
*/

const QR_STEPS_COMPONENTS = [
  Step1Partial,
  Step2QrPartial,
  Step4Partial
];
const GUPSHUP_STEPS_COMPONENTS = [
  Step1Partial,
  Step2GupshupPartial,
  Step3Partial,
  Step4Partial
];

const CreateCampaignsSidebar = ({ refetchCampaigns }) => {
  const [steps, setStepsComponents] = React.useState([]);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [isSending, setIsSending] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [activeValidation, setActiveValidation] = React.useState(false);
  const [campaignType, setIntegrationType] = React.useState(null);
  const [templateFilters, setTemplateFilters] = React.useState({
    search: '',
    template_type: null,
    team_id: null,
    openAccordion: false
  });
  const dispatch = useDispatch();
  const istLastStep = currentStep === steps.length - 1;
  const { current_retailer } = useSelector(
    (reduxState) => reduxState.createCampaignReducer
  );

  const retailer_info = useSelector(
    (reduxState) => reduxState.retailerUsersReducer.retailer_info
  );

  const { t } = useTranslation('createCampaign');
  const campaignMinDate = getDateInTimezone(new Date(), current_retailer?.catalogs?.timezone);

  // TODO: Setear traducciones
  const createCampaignSchemaQR = object().shape({
    name: string().required(t('CreateCampaignsSidebar.qrFields.campaugnNameRequired')),
    send_at: string().required(t('CreateCampaignsSidebar.qrFields.sendAtRequired')),
    contact_group_id: string().required(t('CreateCampaignsSidebar.qrFields.contactGroupRequired')),
    // TODO: condicionar requerido por lo menos uno de los dos template_text | file
    message_values: object().test(
      'is-params-template-filled',
      t('CreateCampaignsSidebar.qrFields.templateTextOrFieldRequired'),
      (value) => {
        if (!value?.template_text && !value?.file) {
          return false;
        }
        return true;
      }
    ),
    file: string()
  });

  // TODO: Setear traducciones
  const createCampaignSchemaGupshup = object().shape({
    name: string().required(t('CreateCampaignsSidebar.gupshupFields.campaugnNameRequired')),
    send_at: date().min(campaignMinDate, 'Fecha de envío menor a la actual').required(t('CreateCampaignsSidebar.gupshupFields.sendAtRequired')),
    contact_group_id: string().required(t('CreateCampaignsSidebar.gupshupFields.contactGroupRequired')),
    content_params: array().of(string()),
    // TODO: condicionar si es requerido en base al paso anterior
    file: mixed().test(
      'is-params-template-filled',
      t('CreateCampaignsSidebar.gupshupFields.templateFieldRequired'),
      (value) => {
        if (!value && selectedTemplate?.templateType !== 'text') {
          return false;
        }
        return true;
      }
    ),
    params_template: object().test(
      'is-params-template-filled',
      'Debe completar todos los campos',
      (value) => {
        if (!selectedTemplate?.text || !selectedTemplate?.text.includes('*')) {
          return true;
        }
        if (!value) {
          return false;
        }
        if (Object.keys(value).length === 0) {
          return false;
        }
        if (Object.keys(value).some((key) => value[key] === '')) {
          return false;
        }
        return true;
      }
    )
  });

  const methods = useForm({
    resolver: yupResolver(campaignType === INTEGRATION_TYPES.GUPSHUP.value ? createCampaignSchemaGupshup : createCampaignSchemaQR)
  });

  const nextStep = async () => {
    const fieldsQR = [[
      'name',
      'send_at',
      'contact_group_id'
    ], [
      'message_values'
    ]];
    const fieldsGUPSHUP = [[
      'name',
      'send_at',
      'contact_group_id'
    ],
    [],
    [
      'params_template',
      'file'
    ]];
    const fieldsByTypeAndStep = campaignType === INTEGRATION_TYPES.GUPSHUP.value ? fieldsGUPSHUP[currentStep] : fieldsQR[currentStep];
    await methods.trigger(fieldsByTypeAndStep);
    let shouldPassNextStep = true;
    if (campaignType === INTEGRATION_TYPES.GUPSHUP.value && currentStep === 1 && !selectedTemplate) {
      shouldPassNextStep = false;
    } else {
      fieldsByTypeAndStep?.forEach((field) => {
        const fieldState = methods.getFieldState(field);
        if (fieldState?.error || fieldState?.invalid) {
          shouldPassNextStep = false;
        }
      });
    }

    if (shouldPassNextStep) {
      setActiveValidation(false);
      const nextStepValue = currentStep + 1;
      if (nextStepValue < steps.length) {
        setCurrentStep(nextStepValue);
      } else if (nextStepValue === steps.length) {
        const formValues = methods.getValues();
        if (campaignType === INTEGRATION_TYPES.GUPSHUP.value) {
          saveGupshupCampaign(formValues);
        } else {
          saveQrCampaign(formValues);
        }
      }
    } else {
      setActiveValidation(true);
    }
  };

  const prevStep = () => {
    const prevStepValue = currentStep - 1;
    if (prevStepValue >= 0) {
      setCurrentStep(prevStepValue);
    }
  };

  const validMinDate = () => {
    const { send_at } = methods.getValues();
    const minDate = getDateInTimezone(new Date(), current_retailer?.catalogs?.timezone);
    if (send_at <= minDate) {
      showtoast('La fecha y hora de envío debe ser mayor a la actual para la zona horaria configurada');
      return false;
    }
    return true;
  };

  const saveGupshupCampaign = (wizardValues) => {
    if (!validMinDate()) {
      return;
    }

    const gupshupBody = {
      name: wizardValues.name,
      send_at: serializeCampaignSendAt({ date: wizardValues.send_at }),
      contact_group_id: wizardValues.contact_group_id,
      whatsapp_template_id: selectedTemplate.id,
      campaign_timezone: current_retailer.catalogs.timezone,
      content_params: wizardValues?.params_template
        ? Object.values(wizardValues.params_template)
        : []
    };

    if (wizardValues.file) {
      gupshupBody.file = wizardValues.file;
    }

    createCampaign(gupshupBody);
  };

  const saveQrCampaign = (wizardValues) => {
    if (!validMinDate(wizardValues.send_at)) {
      return;
    }

    const templateText = wizardValues.message_values.template_text;
    const pattern = /{{.+?}}/g;
    const matches = templateText.match(pattern);

    const qrBody = {
      name: wizardValues.name,
      send_at: serializeCampaignSendAt({ date: wizardValues.send_at }),
      contact_group_id: wizardValues.contact_group_id,
      template_text: templateText,
      campaign_timezone: current_retailer.catalogs.timezone,
      content_params: matches
    };

    if (wizardValues.message_values.file) {
      qrBody.file = wizardValues.message_values.file;
    }

    createCampaign(qrBody);
  };

  const createCampaign = (body) => {
    const formBody = jsonToFormData(body, 'campaign');
    setIsSending(true);
    createCampaignModel.createCampaign(formBody, ENV.SLUG).then((data) => {
      refetchCampaigns();
      showtoast(data.message);
    }).catch(async (err) => {
      showtoast(err.message);
      dispatch({ type: 'LOAD_DATA_FAILURE', err });
      if (err.message) {
        err.message?.campaign?.map((error) => showtoast(error));
      }
    }).finally(() => {
      setIsSending(false);
    });
  };

  const title = t('CreateCampaignsSidebar.stepTitle', { currentStep: currentStep + 1, totalSteps: steps.length });

  useEffect(() => {
    setStepsComponents(
      campaignType === INTEGRATION_TYPES.GUPSHUP.value ? GUPSHUP_STEPS_COMPONENTS : QR_STEPS_COMPONENTS
    );
  }, [campaignType]);

  const onSelectProp = (optionKey, value) => {
    switch (optionKey) {
      case 'template':
        setSelectedTemplate(value);
        methods.resetField('params_template');
        break;
      default:
        break;
    }
  };
  const renderStep = () => {
    const StepComponent = steps[currentStep];
    return (
      <StepComponent
        methods={methods}
        onSelectProp={onSelectProp}
        selectedTemplate={selectedTemplate}
        templateFilters={templateFilters}
        setTemplateFilters={setTemplateFilters}
        activeValidation={activeValidation}
        campaignType={campaignType}
      />
    );
  };

  useEffect(() => {
    if (campaignType) {
      dispatch(campaignType === INTEGRATION_TYPES.GUPSHUP.value ? getGupshupCatalogs() : getQrCatalogs());
    }
  }, [campaignType]);

  useEffect(() => {
    if (!campaignType && retailer_info) {
      setIntegrationType(retailer_info.qr_integrated ? INTEGRATION_TYPES.QR.value : INTEGRATION_TYPES.GUPSHUP.value);
    }
  }, [retailer_info.qr_integrated]);

  if (!steps.length || !current_retailer || !campaignType) {
    return null;
  }

  return (
    <RightModalLayout title={title} noContainerPadding className="tw-h-full">
      {/* //TODO Agregar controller form a FileUploaderMC al FormProvierMC */}
      <FormProvierMC methods={methods} className="tw-h-full tw-flex tw-flex-col">
        <div className="tw-px-6 tw-py-6 tw-flex-1">{renderStep()}</div>
        <div className="tw-p-6 tw-italic tw-text-body">
          {currentStep === 0 && (
            <div className="tw-pb-2">
              {t('CreateCampaignsSidebar.clientListAlert')}
            </div>
          )}
          {currentStep > 0 && campaignType === INTEGRATION_TYPES.QR.value ? (
            <div className="tw-pb-2">
              {t(
                'CreateCampaignsSidebar.qrMaxChunckSizeAlert',
                { chunkSize: current_retailer?.catalogs?.qr_campaign_chunk_size }
              )}
            </div>
          ) : null}
          {currentStep > 0 && campaignType !== INTEGRATION_TYPES.QR.value ? (
            <div className="tw-pb-2">
              <span className="tw-italic">
                {t('CreateCampaignsSidebar.gupshupRecomendation')}
              </span>
              <a className="tw-text-blue-500" target="_blank" href="https://support.mercately.com/es/articles/9745750-recomendaciones-para-enviar-campanas">
                {t('CreateCampaignsSidebar.here')}
              </a>
            </div>
          ) : null}
        </div>
        <div className="tw-flex tw-flex-row tw-w-full tw-border-t tw-border-gray-300 tw-py-4 tw-px-6 tw-sticky tw-bottom-0 tw-bg-white">
          <div className="tw-flex tw-flex-col tw-flex-1 tw-items-end tw-pr-2">
            {currentStep > 0 && (
              <ButtonMC
                text={t('CreateCampaignsSidebar.prev')}
                variant="GRAY"
                onClick={prevStep}
                disabled={isSending}
              />
            )}
          </div>
          <div className="tw-flex tw-flex-col tw-flex-1 tw-items-start tw-pl-2">
            <ButtonMC
              text={istLastStep ? t('CreateCampaignsSidebar.program') : t('CreateCampaignsSidebar.next')}
              onClick={nextStep}
              type="button"
              disabled={!current_retailer?.catalogs?.timezone || isSending}
            />
          </div>
        </div>
      </FormProvierMC>
    </RightModalLayout>
  );
};

export default CreateCampaignsSidebar;
